import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { Rng } from "../rng";
import { flat, Tone, shade } from "../materials";
import { blobGeometry, segmentGeometry } from "../geometry";
import { Slot, slotNormal } from "../palette";

export interface TreeAsset {
  group: THREE.Group;
  sway(time: number): void;
}

export interface BroadleafSpecies {
  kind: "broadleaf";
  height: [number, number];
  trunkRadius: [number, number];
  trunkShare: number;
  trunkRings: number;
  depth: number;
  children: [number, number];
  branchAngle: [number, number];
  lengthRatio: [number, number];
  radiusRatio: number;
  upBias: number;
  wobble: number;
  minRadius: number;
  sides: number;
  foliageStart: number;
  clusters: [number, number];
  clusterRadius: [number, number];
  clusterJitter: number;
  trunkTone: number;
  foliageTone: number;
  tintRange: [number, number];
}

export interface ConiferSpecies {
  kind: "conifer";
  height: [number, number];
  trunkRadius: [number, number];
  whorls: [number, number];
  dropAngle: [number, number];
  branchShare: number;
  sides: number;
  trunkTone: number;
  foliageTone: number;
  tintRange: [number, number];
}

export type Species = BroadleafSpecies | ConiferSpecies;

const OAK: BroadleafSpecies = {
  kind: "broadleaf",
  height: [2.9, 4.3],
  trunkRadius: [0.19, 0.3],
  trunkShare: 0.32,
  trunkRings: 3,
  depth: 4,
  children: [2, 4],
  branchAngle: [0.45, 0.95],
  lengthRatio: [0.62, 0.8],
  radiusRatio: 0.66,
  upBias: 0.18,
  wobble: 0.07,
  minRadius: 0.016,
  sides: 9,
  foliageStart: 2,
  clusters: [4, 6],
  clusterRadius: [0.26, 0.46],
  clusterJitter: 0.1,
  trunkTone: Tone.bark,
  foliageTone: Tone.linen,
  tintRange: [0.3, 0.58],
};

const BIRCH: BroadleafSpecies = {
  kind: "broadleaf",
  height: [3.4, 4.9],
  trunkRadius: [0.1, 0.16],
  trunkShare: 0.44,
  trunkRings: 4,
  depth: 4,
  children: [2, 3],
  branchAngle: [0.3, 0.7],
  lengthRatio: [0.58, 0.74],
  radiusRatio: 0.6,
  upBias: 0.3,
  wobble: 0.05,
  minRadius: 0.012,
  sides: 8,
  foliageStart: 3,
  clusters: [3, 5],
  clusterRadius: [0.17, 0.28],
  clusterJitter: 0.14,
  trunkTone: Tone.paper,
  foliageTone: Tone.bone,
  tintRange: [0.02, 0.2],
};

const HAWTHORN: BroadleafSpecies = {
  kind: "broadleaf",
  height: [1.9, 2.9],
  trunkRadius: [0.13, 0.2],
  trunkShare: 0.24,
  trunkRings: 2,
  depth: 3,
  children: [2, 4],
  branchAngle: [0.55, 1.05],
  lengthRatio: [0.66, 0.84],
  radiusRatio: 0.68,
  upBias: 0.08,
  wobble: 0.09,
  minRadius: 0.014,
  sides: 8,
  foliageStart: 1,
  clusters: [3, 5],
  clusterRadius: [0.18, 0.3],
  clusterJitter: 0.22,
  trunkTone: Tone.bark,
  foliageTone: Tone.ash,
  tintRange: [0.44, 0.7],
};

const WILLOW: BroadleafSpecies = {
  kind: "broadleaf",
  height: [2.6, 3.8],
  trunkRadius: [0.11, 0.17],
  trunkShare: 0.36,
  trunkRings: 3,
  depth: 3,
  children: [3, 5],
  branchAngle: [0.75, 1.25],
  lengthRatio: [0.68, 0.86],
  radiusRatio: 0.62,
  upBias: -0.05,
  wobble: 0.11,
  minRadius: 0.012,
  sides: 8,
  foliageStart: 1,
  clusters: [3, 5],
  clusterRadius: [0.2, 0.34],
  clusterJitter: 0.17,
  trunkTone: Tone.slate,
  foliageTone: Tone.linen,
  tintRange: [0.08, 0.3],
};

const PINE: ConiferSpecies = {
  kind: "conifer",
  height: [4.2, 6.4],
  trunkRadius: [0.17, 0.26],
  whorls: [9, 14],
  dropAngle: [0.16, 0.34],
  branchShare: 0.32,
  sides: 8,
  trunkTone: Tone.bark,
  foliageTone: Tone.ash,
  tintRange: [0.5, 0.66],
};

export const SPECIES = { OAK, BIRCH, HAWTHORN, WILLOW, PINE } as const;

const UP = new THREE.Vector3(0, 1, 0);

const perpendicular = (direction: THREE.Vector3, azimuth: number): THREE.Vector3 => {
  const reference = Math.abs(direction.y) > 0.94 ? new THREE.Vector3(1, 0, 0) : UP;
  const right = new THREE.Vector3().crossVectors(direction, reference).normalize();
  const forward = new THREE.Vector3().crossVectors(direction, right).normalize();
  return right.multiplyScalar(Math.cos(azimuth)).addScaledVector(forward, Math.sin(azimuth)).normalize();
};

interface Crown {
  branches: THREE.BufferGeometry[];
  foliage: THREE.BufferGeometry[];
}

function addClusters(
  rng: Rng,
  crown: Crown,
  at: THREE.Vector3,
  species: BroadleafSpecies,
  scale: number,
): void {
  const count = rng.int(species.clusters[0], species.clusters[1] + 1);
  const spread = rng.range(0.5, 0.85) * scale;
  for (let i = 0; i < count; i++) {
    const radius = rng.range(species.clusterRadius[0], species.clusterRadius[1]) * scale;
    const blob = blobGeometry(radius, 2, species.clusterJitter, rng);
    const angle = rng.range(0, Math.PI * 2);
    const lift = rng.range(-0.35, 1);
    const planar = Math.sqrt(Math.max(0, 1 - lift * lift * 0.6));
    blob.translate(
      at.x + Math.cos(angle) * spread * planar,
      at.y + lift * spread * 0.8,
      at.z + Math.sin(angle) * spread * planar,
    );
    crown.foliage.push(blob);
  }
}

function grow(
  rng: Rng,
  crown: Crown,
  from: THREE.Vector3,
  direction: THREE.Vector3,
  length: number,
  radius: number,
  depth: number,
  species: BroadleafSpecies,
): void {
  const rings = depth === 0 ? species.trunkRings : 1;
  const endRadius = radius * species.radiusRatio;
  const step = length / rings;

  let cursor = from.clone();
  let heading = direction.clone();
  let currentRadius = radius;

  for (let i = 0; i < rings; i++) {
    const nextRadius = radius + (endRadius - radius) * ((i + 1) / rings);
    const to = cursor.clone().addScaledVector(heading, step);
    crown.branches.push(segmentGeometry(cursor, to, currentRadius, nextRadius, species.sides));
    cursor = to;
    currentRadius = nextRadius;
    heading
      .add(
        new THREE.Vector3(
          rng.range(-species.wobble, species.wobble),
          rng.range(-species.wobble * 0.3, species.wobble * 0.6),
          rng.range(-species.wobble, species.wobble),
        ),
      )
      .normalize();
  }

  const exhausted = depth >= species.depth || currentRadius <= species.minRadius;
  if (exhausted) {
    addClusters(rng, crown, cursor, species, 1);
    return;
  }

  const children = rng.int(species.children[0], species.children[1] + 1);
  for (let i = 0; i < children; i++) {
    const azimuth = (i / children) * Math.PI * 2 + rng.range(-0.6, 0.6);
    const axis = perpendicular(heading, azimuth);
    const angle = rng.range(species.branchAngle[0], species.branchAngle[1]);
    const childDirection = heading.clone().applyAxisAngle(axis, angle);
    childDirection.y += species.upBias;
    childDirection.normalize();

    grow(
      rng,
      crown,
      cursor,
      childDirection,
      length * rng.range(species.lengthRatio[0], species.lengthRatio[1]),
      currentRadius * species.radiusRatio,
      depth + 1,
      species,
    );
  }

  if (depth >= species.foliageStart) {
    const along = cursor.clone().addScaledVector(heading, -length * 0.3);
    addClusters(rng, crown, along, species, 0.78);
  }
}

function buildConifer(rng: Rng, species: ConiferSpecies): { crown: Crown; height: number } {
  const height = rng.range(species.height[0], species.height[1]);
  const trunkRadius = rng.range(species.trunkRadius[0], species.trunkRadius[1]);
  const crown: Crown = { branches: [], foliage: [] };

  const trunkSegments = 5;
  let cursor = new THREE.Vector3(0, 0, 0);
  let radius = trunkRadius;
  for (let i = 0; i < trunkSegments; i++) {
    const to = cursor.clone().add(
      new THREE.Vector3(rng.range(-0.02, 0.02), height / trunkSegments, rng.range(-0.02, 0.02)),
    );
    crown.branches.push(segmentGeometry(cursor, to, radius, radius * 0.82, species.sides));
    cursor = to;
    radius *= 0.82;
  }

  const whorls = rng.int(species.whorls[0], species.whorls[1] + 1);
  const baseY = height * 0.17;
  const groundY = 0.3;

  for (let w = 1; w <= whorls; w++) {
    const t = w / (whorls + 1);
    const y = baseY + (height - baseY) * t;
    const spread = 1 - t;
    const arms = Math.max(3, Math.round(3 + spread * 6));
    const reach = height * species.branchShare * (0.38 + spread * 0.62) * rng.range(0.88, 1.1);
    const base = rng.range(0, Math.PI * 2);
    const drop = rng.range(species.dropAngle[0], species.dropAngle[1]);

    for (let b = 0; b < arms; b++) {
      const azimuth = base + (b / arms) * Math.PI * 2 + rng.range(-0.2, 0.2);
      const direction = new THREE.Vector3(Math.cos(azimuth), -drop, Math.sin(azimuth)).normalize();

      const floor = direction.y < -0.02 ? (y - groundY) / -direction.y : reach;
      const armReach = Math.max(0.28, Math.min(reach, floor));

      const start = new THREE.Vector3(0, y, 0);
      const end = start.clone().addScaledVector(direction, armReach);
      crown.branches.push(segmentGeometry(start, end, radius * 0.6, radius * 0.14, 6));

      const needles = rng.int(2, 4);
      for (let n = 0; n < needles; n++) {
        const along = start.clone().lerp(end, (n + 1) / (needles + 1));
        const clusterRadius = Math.min(armReach * rng.range(0.3, 0.44), 0.6);
        const blob = blobGeometry(clusterRadius, 2, 0.15, rng);
        blob.scale(1, rng.range(0.58, 0.8), 1);
        blob.translate(
          along.x + rng.range(-0.04, 0.04),
          along.y + rng.range(-0.02, 0.05),
          along.z + rng.range(-0.04, 0.04),
        );
        crown.foliage.push(blob);
      }
    }
  }

  const tip = blobGeometry(height * 0.062, 2, 0.15, rng);
  tip.scale(1, 1.5, 1);
  tip.translate(rng.range(-0.02, 0.02), height * 0.98, rng.range(-0.02, 0.02));
  crown.foliage.push(tip);

  return { crown, height };
}

function assemble(
  rng: Rng,
  crown: Crown,
  trunkTone: number,
  foliageTone: number,
  variant: number,
  swayAmount: number,
): TreeAsset {
  const group = new THREE.Group();
  const branches = crown.branches.filter((geometry) => geometry.getAttribute("position"));
  const foliage = crown.foliage.filter((geometry) => geometry.getAttribute("position"));

  if (branches.length > 0) {
    const merged = mergeGeometries(branches, false);
    if (merged) {
      const solid = new THREE.Mesh(merged, flat(shade(trunkTone, rng.range(-0.06, 0.06))));
      solid.castShadow = true;
      solid.receiveShadow = true;
      solid.userData.slot = slotNormal(Slot.bark);
      solid.userData.variant = 0.5;
      group.add(solid);
    }
  }

  let foliageMesh: THREE.Mesh | null = null;
  if (foliage.length > 0) {
    const merged = mergeGeometries(foliage, false);
    if (merged) {
      foliageMesh = new THREE.Mesh(merged, flat(shade(foliageTone, rng.range(-0.08, 0.08))));
      foliageMesh.castShadow = true;
      foliageMesh.receiveShadow = true;
      foliageMesh.layers.enable(1);
      foliageMesh.userData.slot = slotNormal(Slot.foliage);
      foliageMesh.userData.variant = variant;
      group.add(foliageMesh);
    }
  }

  const phase = rng.range(0, Math.PI * 2);
  const rate = rng.range(0.5, 1.1);
  const crown3 = foliageMesh;

  return {
    group,
    sway: (time: number): void => {
      if (!crown3) return;
      const wave = Math.sin(time * rate + phase);
      crown3.rotation.z = wave * swayAmount;
      crown3.rotation.x = Math.cos(time * rate * 0.77 + phase) * swayAmount * 0.6;
    },
  };
}

export function buildTree(rng: Rng, species: Species = OAK): TreeAsset {
  if (species.kind === "conifer") {
    const { crown } = buildConifer(rng, species);
    const variant = rng.fork(0x51ed).range(species.tintRange[0], species.tintRange[1]);
    return assemble(rng, crown, species.trunkTone, species.foliageTone, variant, 0.012);
  }

  const height = rng.range(species.height[0], species.height[1]);
  const trunkRadius = rng.range(species.trunkRadius[0], species.trunkRadius[1]);
  const crown: Crown = { branches: [], foliage: [] };
  const start = new THREE.Vector3(0, 0, 0);
  const heading = new THREE.Vector3(
    rng.range(-0.06, 0.06),
    1,
    rng.range(-0.06, 0.06),
  ).normalize();

  grow(rng, crown, start, heading, height * species.trunkShare, trunkRadius, 0, species);
  const variant = rng.fork(0x51ed).range(species.tintRange[0], species.tintRange[1]);
  return assemble(rng, crown, species.trunkTone, species.foliageTone, variant, 0.018);
}
